import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listLedger, type LedgerResponse } from "../../api/codeReview";
import { allPacksStats, type BobStatsResponse, type PackPosture } from "../../api/bobReports";
import { ledgerState } from "../../lib/ledgerFormat";

/** What the pack rail shows for one project. Counts are absent unless Bob has reviewed it. */
export interface PackSummary {
  repo?: string;
  branch?: string;
  open?: number;
  block?: number;
  /** The project has no place in the rail: it has no GitHub repo, or Bob does not review it. */
  offRail: boolean;
}

// Open counts are current whatever the window, so the rail asks for the smallest one.
const RAIL_STATS_DAYS = 30;

export function summarize(r: LedgerResponse): PackSummary {
  const state = ledgerState(r);
  const reviewed = state === "clean" || state === "has-findings";
  const open = r.counts?.open;
  return {
    repo: r.repo,
    branch: r.branch ?? r.last_run?.branch,
    open: reviewed && open ? Object.values(open).reduce((sum, n) => sum + n, 0) : undefined,
    block: reviewed && open && typeof open.BLOCK === "number" ? open.BLOCK : undefined,
    offRail: r.note === "no_github_repo",
  };
}

export function summarizePosture(p: PackPosture): PackSummary {
  const total = typeof p.open_total === "number" ? p.open_total : undefined;
  const reviewed = p.last_completed_run != null || (total !== undefined && total > 0);
  return {
    repo: p.repo,
    branch: p.branch,
    open: reviewed ? total : undefined,
    block: reviewed && typeof p.open?.BLOCK === "number" ? p.open.BLOCK : undefined,
    offRail: false,
  };
}

/**
 * Each project's summary from one all-packs stats answer, keyed by every project
 * the server labelled each repo with, or null when the answer has no per-pack list.
 */
export function summariesFromStats(stats: BobStatsResponse): Map<string, PackSummary> | null {
  if (!Array.isArray(stats.per_repo)) return null;
  const byProject = new Map<string, PackSummary>();
  for (const p of stats.per_repo) {
    if (!p) continue;
    const owners = Array.isArray(p.projects) ? p.projects.map((o) => o?.id) : [p.project];
    for (const id of owners) {
      if (typeof id === "string" && !byProject.has(id)) byProject.set(id, summarizePosture(p));
    }
  }
  return byProject;
}

/**
 * The rail's counts from one all-packs stats read. A project absent from it is
 * one Bob does not review, so it leaves the rail. When that read fails, one
 * single-row ledger read per project stands in. The project on screen is
 * skipped either way: its summary comes from the panel's own read. Nothing is
 * read again until `refresh` is called.
 */
export interface PackReaders {
  stats: (days: typeof RAIL_STATS_DAYS) => Promise<BobStatsResponse>;
  ledger: (project: string) => Promise<LedgerResponse>;
}

const READERS: PackReaders = {
  stats: allPacksStats,
  ledger: (project) => listLedger(project, "open", { limit: 1, offset: 0 }),
};

export function usePackSummaries(ids: readonly string[], skip: string, readers: PackReaders = READERS): {
  summaries: ReadonlyMap<string, PackSummary>;
  refresh: () => void;
} {
  const [fromStats, setFromStats] = useState<Map<string, PackSummary> | null>(null);
  const [fallback, setFallback] = useState(false);
  const [fromLedger, setFromLedger] = useState<Map<string, PackSummary>>(() => new Map());
  const asked = useRef(new Set<string>());
  const [round, setRound] = useState(0);
  const roundRef = useRef(0);
  roundRef.current = round;
  const key = ids.join("\n");

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    const my = round;
    const fresh = () => alive.current && my === roundRef.current;
    readers.stats(RAIL_STATS_DAYS)
      .then((stats) => {
        if (!fresh()) return;
        const map = summariesFromStats(stats);
        setFromStats(map);
        setFallback(map === null);
      })
      .catch(() => {
        if (!fresh()) return;
        setFromStats(null);
        setFallback(true);
      });
  }, [round, readers]);

  useEffect(() => {
    if (!fallback) return;
    const my = round;
    for (const id of key === "" ? [] : key.split("\n")) {
      if (id === skip || asked.current.has(id)) continue;
      asked.current.add(id);
      readers.ledger(id)
        .then((r) => {
          if (alive.current && my === roundRef.current) setFromLedger((prev) => new Map(prev).set(id, summarize(r)));
        })
        .catch(() => { /* the rail shows the pack without numbers */ });
    }
  }, [fallback, key, skip, round, readers]);

  const refresh = useCallback(() => {
    asked.current = new Set();
    setRound((n) => n + 1);
  }, []);

  const summaries = useMemo<ReadonlyMap<string, PackSummary>>(() => {
    if (fallback || fromStats === null) return fromLedger;
    const listed = key === "" ? [] : key.split("\n");
    return new Map(listed.map((id) => [id, fromStats.get(id) ?? { offRail: true }]));
  }, [fallback, fromStats, fromLedger, key]);
  return { summaries, refresh };
}
